"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useSignIn } from "@clerk/nextjs/legacy";
import { useUser } from "@clerk/nextjs";
import { CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { clerkErrorToUserMessage, type AuthErrorMessage } from "@/lib/auth/clerk-error-message";

const benefits = [
  "Build landing pages in under 60 seconds",
  "5 proven, high-converting templates",
  "Built-in lead capture & analytics",
  "Publish to your own subdomain instantly",
];

export default function SignInPage() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const { isSignedIn } = useUser();
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<AuthErrorMessage | null>(null);
  const [loading, setLoading] = useState(false);

  // PHU-225: if the page mounts and we already have a Clerk session, the
  // user is signed in — bounce them to /dashboard. Skip this redirect
  // for sign-ins started in *this* tab — the handler below owns the
  // navigation in that case, and we don't want a setActive → isSignedIn
  // race to slip in an unwanted router.replace before our own push fires.
  const localSignInInFlight = useRef(false);
  useEffect(() => {
    if (isSignedIn && !localSignInInFlight.current) {
      router.replace("/dashboard");
    }
  }, [isSignedIn, router]);

  // PHU-224: shared error handler — log raw + map to UX copy. We use
  // console.warn (not console.error) because these are *expected* failure
  // modes (wrong password, account-not-found, OAuth cancelled, etc.) —
  // logging at error level would trigger Next.js 16's dev-overlay popup
  // for every typo'd password, which is just noise.
  function reportError(err: unknown, source: "email" | "oauth") {
    // Verbose breakdown so we can see exactly what Clerk's throwing —
    // helps diagnose mapper misses without DevTools spelunking.
    const e = err as Record<string, unknown> | null;
    console.warn(`[sign-in:${source}] Clerk error`, {
      err,
      hasErrorsArray: Array.isArray(e?.errors),
      errorsLength: Array.isArray(e?.errors) ? e.errors.length : null,
      firstCode: Array.isArray(e?.errors) && e.errors.length
        ? (e.errors[0] as { code?: string })?.code
        : null,
      topLevelCode: typeof e?.code === "string" ? e.code : null,
      topLevelMessage: typeof e?.message === "string" ? e.message : null,
      keys: e && typeof e === "object" ? Object.keys(e) : null,
    });
    setError(clerkErrorToUserMessage(err));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setError(null);
    setLoading(true);
    localSignInInFlight.current = true;

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        router.push("/dashboard");
        return;
      }

      // Not complete — Clerk is asking for additional factors. Surface a
      // friendly message rather than silently leaving the user on a stale
      // form. (We don't yet support MFA flows here.)
      setError({
        title: "More verification is needed",
        detail: "We can't complete sign-in from this screen yet. Please contact support.",
      });
    } catch (err) {
      // PHU-225: Clerk may have created a session even though a downstream
      // step threw. Treat that as success.
      if (signIn?.status === "complete" && signIn.createdSessionId) {
        try {
          await setActive({ session: signIn.createdSessionId });
          router.push("/dashboard");
          return;
        } catch (innerErr) {
          console.error("[sign-in:email] setActive after silent-complete threw", innerErr);
        }
      }
      reportError(err, "email");
    } finally {
      setLoading(false);
    }
  }

  async function handleOAuth(strategy: "oauth_google" | "oauth_github") {
    if (!isLoaded) return;
    setError(null);
    localSignInInFlight.current = true;
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: "/sign-in/sso-callback",
        redirectUrlComplete: "/dashboard",
      });
    } catch (err) {
      // PHU-225: defensive — if the session was actually created before
      // the redirect threw, route to the dashboard instead of pretending
      // the user is still signed out.
      if (signIn?.status === "complete" && signIn.createdSessionId) {
        try {
          await setActive({ session: signIn.createdSessionId });
          router.push("/dashboard");
          return;
        } catch (innerErr) {
          console.error("[sign-in:oauth] setActive after silent-complete threw", innerErr);
        }
      }
      reportError(err, "oauth");
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between bg-[#0F172A] p-12">
        <div>
          <Link href="/" className="flex items-center">
            <Image
              src="/funnlr_logo.png"
              alt="Funnlr"
              width={200}
              height={48}
              className="h-12 w-auto brightness-0 invert"
            />
          </Link>

          <div className="mt-16">
            <h1 className="font-heading text-3xl font-bold text-white">
              Welcome back.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Log in to manage your funnels, track leads, and grow your
              business.
            </p>
          </div>

          <ul className="mt-10 flex flex-col gap-4">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-400" />
                <span className="text-sm text-slate-300">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-slate-500">
          Build funnels that convert — no code required.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Link href="/" className="flex items-center">
              <Image
                src="/funnlr_logo.png"
                alt="Funnlr"
                width={120}
                height={32}
                className="h-8 w-auto"
              />
            </Link>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Log in to Funnlr
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Welcome back — pick up where you left off.
          </p>

          {/* OAuth buttons */}
          <div className="mt-6 flex flex-col gap-2.5">
            <button
              type="button"
              onClick={() => handleOAuth("oauth_google")}
              className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {/* Email + password form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            <div>
              <label htmlFor="email" className="mb-1 block text-xs font-medium text-slate-700">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-xs font-medium text-slate-700">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && !error.silent && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <p className="font-medium">{error.title}</p>
                {error.detail && <p className="mt-0.5 text-red-600">{error.detail}</p>}
                {error.action && (
                  error.action.flow === "oauth_google" ? (
                    <button
                      type="button"
                      onClick={() => handleOAuth("oauth_google")}
                      className="mt-2 inline-flex items-center text-sm font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                    >
                      {error.action.label} →
                    </button>
                  ) : error.action.href ? (
                    <Link
                      href={error.action.href}
                      className="mt-2 inline-flex items-center text-sm font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                    >
                      {error.action.label} →
                    </Link>
                  ) : null
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-500">
            Don&apos;t have an account?{" "}
            <Link
              href="/sign-up"
              className="font-medium text-brand-500 hover:text-brand-600"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
